import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../lib/supabase'
import type { Database } from '../../../../types/'
import type { AppNotification } from '../../../../types/shared/common'
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js'

type NotificationRow = Database['public']['Tables']['notifications']['Row']

function normalizeEventType(eventType: string): AppNotification['event_type'] {
  if (
    eventType === 'herstel_message' ||
    eventType === 'herstel_ingediend' ||
    eventType === 'factuur_sent_email' ||
    eventType === 'factuur_reminder_email' ||
    eventType === 'offerte_accepted' ||
    eventType === 'offerte_rejected' ||
    eventType === 'locatie_access_requested' ||
    eventType === 'locatie_access_approved' ||
    eventType === 'locatie_access_denied' ||
    eventType === 'locatie_access_withdrawn' ||
    eventType === 'documenten_opgevraagd'
  ) {
    return eventType
  }
  return 'herstel_message'
}

function mapNotificationRow(row: NotificationRow): AppNotification {
  return {
    ...row,
    event_type: normalizeEventType(row.event_type),
    metadata: (row.metadata ?? {}) as Record<string, unknown>,
  }
}

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<AppNotification[]>([])
  const loading = ref(false)
  let realtimeChannel: RealtimeChannel | null = null
  let currentUserId: string | null = null

  const unseenCount = computed(() => notifications.value.filter((n) => !n.seen).length)

  async function fetchNotifications() {
    loading.value = true
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) return

      currentUserId = user.id

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) {
        console.error('Failed to fetch notifications:', error)
        return
      }

      notifications.value = (data || []).map(mapNotificationRow)
    } finally {
      loading.value = false
    }
  }

  function subscribeToRealtime() {
    if (!currentUserId) return

    // Clean up existing channel before creating a new one
    if (realtimeChannel) {
      realtimeChannel.unsubscribe()
      realtimeChannel = null
    }

    realtimeChannel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${currentUserId}`,
        },
        (payload: RealtimePostgresChangesPayload<NotificationRow>) => {
          if (payload.eventType === 'INSERT') {
            const newNotification = mapNotificationRow(payload.new as NotificationRow)
            notifications.value = [newNotification, ...notifications.value].slice(0, 10)
          } else if (payload.eventType === 'UPDATE') {
            const updated = mapNotificationRow(payload.new as NotificationRow)
            const idx = notifications.value.findIndex((n) => n.id === updated.id)
            if (idx !== -1) {
              notifications.value[idx] = updated
            }
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as Partial<NotificationRow>)?.id
            if (deletedId) {
              notifications.value = notifications.value.filter((n) => n.id !== deletedId)
            }
          }
        },
      )
      .subscribe()
  }

  function unsubscribe() {
    if (realtimeChannel) {
      realtimeChannel.unsubscribe()
      realtimeChannel = null
    }
  }

  async function markAsSeen(id: string) {
    const notification = notifications.value.find((n) => n.id === id)
    if (!notification || notification.seen) return

    notification.seen = true
    notification.seen_at = new Date().toISOString()

    const { error } = await supabase
      .from('notifications')
      .update({ seen: true, seen_at: new Date().toISOString() })
      .eq('id', id)

    if (error) {
      console.error('Failed to mark notification as seen:', error)
      notification.seen = false
      notification.seen_at = null
    }
  }

  async function markAllSeen() {
    const unseenIds = notifications.value.filter((n) => !n.seen).map((n) => n.id)
    if (unseenIds.length === 0) return

    const now = new Date().toISOString()
    notifications.value.forEach((n) => {
      if (!n.seen) {
        n.seen = true
        n.seen_at = now
      }
    })

    const { error } = await supabase
      .from('notifications')
      .update({ seen: true, seen_at: now })
      .in('id', unseenIds)

    if (error) {
      console.error('Failed to mark all as seen:', error)
      await fetchNotifications()
    }
  }

  function getDeepLink(notification: AppNotification): string {
    switch (notification.event_type) {
      case 'herstel_ingediend':
      case 'herstel_message':
      case 'documenten_opgevraagd':
        return typeof notification.metadata?.portal_path === 'string'
          ? notification.metadata.portal_path
          : notification.rapport_id
            ? `/rapport/${notification.rapport_id}`
            : '/rapporten'
      case 'offerte_accepted':
      case 'offerte_rejected':
        if (notification.offerte_id) {
          return `/offerte/${notification.offerte_id}`
        }
        return '/offertes'
      case 'factuur_sent_email':
      case 'factuur_reminder_email': {
        const portalPath = typeof notification.metadata?.portal_path === 'string' ? notification.metadata.portal_path : null
        return portalPath || '/facturen'
      }
      case 'locatie_access_requested': {
        const portalPath = typeof notification.metadata?.portal_path === 'string' ? notification.metadata.portal_path : null
        return portalPath || '/'
      }
      case 'locatie_access_approved':
      case 'locatie_access_denied':
      case 'locatie_access_withdrawn': {
        const frontendPath = typeof notification.metadata?.frontend_path === 'string' ? notification.metadata.frontend_path : null
        return frontendPath || '/'
      }
      default:
        return '/'
    }
  }

  async function init() {
    await fetchNotifications()
    subscribeToRealtime()
  }

  return {
    notifications,
    loading,
    unseenCount,
    fetchNotifications,
    subscribeToRealtime,
    unsubscribe,
    markAsSeen,
    markAllSeen,
    getDeepLink,
    init,
  }
})
